import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { authStore } from '../modules/auth/auth.store';
import { router } from '../app/router';
import { showToast } from '../shared/components/Toast';
import { escapeHTML } from '../shared/lib/format';

/* ------------------------------------------------------------------ */
// Types
/* ------------------------------------------------------------------ */

interface Plan {
  id: string;
  label: string;
  amountKobo: number;
  priceFormatted: string;
  tagline: string;
  features: string[];
  recommended?: boolean;
}

interface CheckoutData {
  authorizationUrl: string;
  reference: string;
}

/* ------------------------------------------------------------------ */
// Data
/* ------------------------------------------------------------------ */

function formatNaira(kobo: number): string {
  return '₦' + (kobo / 100).toLocaleString('en-NG');
}

const PLANS: Plan[] = [
  {
    id: '1month',
    label: '1 Month',
    amountKobo: 500000,
    priceFormatted: formatNaira(500000),
    tagline: 'Quick connections',
    features: ['WhatsApp Contact Unlock', '100 Daily Swipes', 'Verified VIP Badge'],
  },
  {
    id: '3months',
    label: '3 Months',
    amountKobo: 1250000,
    priceFormatted: formatNaira(1250000),
    tagline: 'Save 20% quarterly',
    features: ['WhatsApp Contact Unlock', 'Unlimited Swipes', 'Profile Boosts', 'Verified VIP Badge'],
    recommended: true,
  },
  {
    id: '6months',
    label: '6 Months',
    amountKobo: 2000000,
    priceFormatted: formatNaira(2000000),
    tagline: 'Best value for serious connections',
    features: ['WhatsApp Contact Unlock', 'Unlimited Swipes', 'Profile Boosts', 'Verified VIP Badge'],
  },
];

/* ------------------------------------------------------------------ */
// Helpers
/* ------------------------------------------------------------------ */

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
}

/* ------------------------------------------------------------------ */
// Sections
/* ------------------------------------------------------------------ */

function renderPremiumState(): string {
  return `
    <main class="section pt-nav">
      <div class="container max-w-md text-center">
        <div class="card card-body">
          <i class="fa-solid fa-crown text-4xl mb-4 text-gold" aria-hidden="true"></i>
          <h2>You're a Zuri VIP!</h2>
          <p class="text-sm text-muted mt-2">
            Enjoy full access to WhatsApp unlocks, unlimited swipes, and premium features.
          </p>
          <button 
            class="btn btn-secondary mt-6" 
            id="vipBrowseBtn"
            data-route="/discover"
          >
            <i class="fa-solid fa-compass" aria-hidden="true"></i> Browse Profiles
          </button>
        </div>
      </div>
    </main>
  `;
}

function renderAuthPrompt(): string {
  return `
    <main class="section pt-nav">
      <div class="container max-w-md text-center">
        <div class="card card-body">
          <i class="fa-solid fa-lock text-4xl mb-4 text-accent" aria-hidden="true"></i>
          <h2>Sign in to Upgrade</h2>
          <p class="text-sm text-muted mt-2 mb-6">
            Create an account or sign in to unlock Zuri Premium and connect on WhatsApp.
          </p>
          <button 
            class="btn btn-primary btn-lg w-full" 
            id="authPromptBtn" 
            data-route="/auth"
            aria-label="Sign in or create an account"
          >
            <i class="fa-solid fa-user" aria-hidden="true"></i> Sign In or Join
          </button>
        </div>
      </div>
    </main>
  `;
}

function renderPlanCard(plan: Plan): string {
  const recommendedBadge = plan.recommended
    ? `<div class="badge badge-gold mb-3" aria-label="Recommended plan">
         <i class="fa-solid fa-star" aria-hidden="true"></i> Most Popular
       </div>`
    : '';

  return `
    <article 
      class="card card-body ${plan.recommended ? 'border-gold shadow-glow' : ''}" 
      aria-labelledby="plan-title-${plan.id}"
    >
      ${recommendedBadge}
      <div class="flex items-baseline justify-between mb-1">
        <h3 id="plan-title-${plan.id}">${escapeHTML(plan.label)}</h3>
        <span class="text-2xl font-bold text-accent">${plan.priceFormatted}</span>
      </div>
      <p class="text-sm text-muted mb-4">${escapeHTML(plan.tagline)}</p>
      
      <ul class="text-sm mb-6 space-y-2" aria-label="Features for ${escapeHTML(plan.label)}">
        ${plan.features.map(f => `
          <li class="flex items-start gap-2">
            <i class="fa-solid fa-check text-accent mt-0.5" aria-hidden="true"></i>
            <span>${escapeHTML(f)}</span>
          </li>
        `).join('')}
      </ul>

      <button 
        class="btn btn-primary w-full js-select-plan" 
        data-plan="${plan.id}"
        aria-label="Choose ${escapeHTML(plan.label)} plan for ${plan.priceFormatted}"
      >
        Choose ${escapeHTML(plan.label)}
      </button>
    </article>
  `;
}

function renderPlansList(): string {
  return `
    <main class="section pt-nav">
      <div class="container max-w-md">
        <div class="section-header text-center">
          <h1>Upgrade to Zuri Premium</h1>
          <p class="section-subtitle text-muted">
            Unlock WhatsApp contacts, unlimited swipes, and your verified VIP badge.
          </p>
        </div>

        <div id="plansList" class="grid gap-4 mt-8" role="list" aria-label="Membership plans">
          ${PLANS.map(plan => `
            <div role="listitem">${renderPlanCard(plan)}</div>
          `).join('')}
        </div>

        <p class="text-xs text-center mt-6 text-muted">
          <i class="fa-solid fa-lock" aria-hidden="true"></i> 
          Secure checkout powered by <strong>Paystack</strong>. Nigerian cards, USSD, and bank transfers accepted.
        </p>
      </div>
    </main>
  `;
}

/* ------------------------------------------------------------------ */
// Main
/* ------------------------------------------------------------------ */

export function renderMembershipPage(container: HTMLElement): () => void {
  const isAuth = authStore?.isAuthenticated ?? false;
  const isPremium = authStore?.isPremium ?? false;

  // Guard: unauthenticated users see an auth prompt instead of plans
  if (!isAuth) {
    container.innerHTML = renderAuthPrompt();
  } else if (isPremium) {
    container.innerHTML = renderPremiumState();
  } else {
    container.innerHTML = renderPlansList();
  }

  /* ---------------------------------------------------------------- */
  // Event wiring with cleanup
  /* ---------------------------------------------------------------- */

  const listeners: Array<{ el: Element; type: string; fn: EventListener }> = [];
  let isProcessing = false;

  // Navigation buttons (auth prompt, VIP state)
  const onNavClick = (e: Event) => {
    const route = (e.currentTarget as HTMLElement).dataset.route;
    if (route) router.navigate(route);
  };

  container.querySelectorAll('[data-route]').forEach(el => {
    el.addEventListener('click', onNavClick);
    listeners.push({ el, type: 'click', fn: onNavClick });
  });

  // If user isn't viewing plans, nothing else to wire
  if (!isAuth || isPremium) {
    return function unmount() {
      listeners.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
      container.innerHTML = '';
    };
  }

  // Scoped button query — never leaks outside this container
  const getPlanButtons = (): NodeListOf<HTMLButtonElement> =>
    container.querySelectorAll('.js-select-plan');

  const onPlanClick = (e: Event) => {
    const btn = e.currentTarget as HTMLButtonElement;
    handlePlanSelect(btn, getPlanButtons());
  };

  getPlanButtons().forEach(btn => {
    btn.addEventListener('click', onPlanClick);
    listeners.push({ el: btn, type: 'click', fn: onPlanClick });
  });

  /* ---------------------------------------------------------------- */
  // Payment flow
  /* ---------------------------------------------------------------- */

  async function handlePlanSelect(
    button: HTMLButtonElement,
    allButtons: NodeListOf<HTMLButtonElement>,
  ): Promise<void> {
    if (isProcessing) return;

    const planId = button.dataset.plan;
    const plan = PLANS.find(p => p.id === planId);
    const user = authStore.user;

    if (!planId || !plan) return;
    if (!user?.email) {
      showToast('Please sign in to upgrade.', 'error');
      return;
    }
    isProcessing = true;
    allButtons.forEach(b => {
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
    });
    const originalText = button.innerHTML;
    button.innerHTML = `<i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i> Preparing checkout…`;

    try {
      const initFn = httpsCallable<{ plan: string; callbackUrl: string }, CheckoutData>(functions, 'initializeCheckout');
      const callbackUrl = `${window.location.origin}/#/membership`;
      const initResult = await initFn({ plan: planId, callbackUrl });
      if (!initResult.data.authorizationUrl) throw new Error('Checkout link was not returned.');

      // Paystack hosts checkout. Only its signed webhook grants premium access.
      window.location.assign(initResult.data.authorizationUrl);
    } catch (err) {
      console.error('Failed to initialize payment', err);
      showToast(`Could not start checkout: ${errorMessage(err, 'Please try again.')}`, 'error');
      resetButtons(allButtons, button, originalText);
      isProcessing = false;
    }
  }

  function resetButtons(
    allButtons: NodeListOf<HTMLButtonElement>,
    activeButton: HTMLButtonElement,
    originalText: string,
  ): void {
    allButtons.forEach(b => {
      b.disabled = false;
      b.removeAttribute('aria-busy');
    });
    activeButton.innerHTML = originalText;
  }

  /* ---------------------------------------------------------------- */
  // Cleanup
  /* ---------------------------------------------------------------- */

  return function unmount() {
    listeners.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
    container.innerHTML = '';
  };
}
